import { useState } from 'react'

import Styles from './ProvideReason.module.scss'

import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { Checkbox } from 'components/Form/Checkbox/Checkbox'
import { CancellationReason } from '../PaymentForm/PaymentForm'
import { Modal } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Input } from 'almanac-ui/TextInputField/Input'
import { TextAreaField } from 'components/Form/TextAreaField'

export interface SelectedReason {
  reason: Reason
  description: string
  subItems?: string[]
}

interface Props {
  onCancel: () => void
  onNext: (selectedReasons: SelectedReason[], additionalFeedback?: string) => void
  initialSelectedReasons?: SelectedReason[]
  initialAdditionalFeedback?: string
}

export enum Reason {
  MissingIntegrations = 'missing_integrations',
  UnreliableContentGeneration = 'unreliable_content_generation',
  UnreliableContentPublishing = 'unreliable_content_publishing',
  PlatformTooSlow = 'platform_too_slow',
  BadAiGenerations = 'bad_ai_generations',
  LackingEditingFeatures = 'lacking_editing_features',
  TooExpensive = 'too_expensive',
  DidNotGetMoreCustomers = 'did_not_get_more_customers',
  TooComplex = 'too_complex',
  SwitchedService = 'switched_service',
  Other = 'other',
}

/** Sub-items shown as checkboxes when parent reason is selected. Keys are included in cancellation_details_comment. */
export const REASON_SUB_ITEMS: Partial<Record<Reason, Record<string, string>>> = {
  [Reason.BadAiGenerations]: {
    image_quality: 'Quality of images',
    video_quality: 'Quality of videos',
    templates_quality: 'Quality of templates',
    content_topics_themes: 'Content topics and themes',
  },
}

const reasonLabels: Record<Reason, string> = {
  [Reason.MissingIntegrations]: 'Missing integrations I need',
  [Reason.UnreliableContentGeneration]: 'Unreliable content generation',
  [Reason.UnreliableContentPublishing]: 'Unreliable content publishing',
  [Reason.PlatformTooSlow]: 'Platform is too slow',
  [Reason.BadAiGenerations]: 'AI generations not good enough',
  [Reason.LackingEditingFeatures]: 'Not enough editing features',
  [Reason.TooExpensive]: 'Too expensive',
  [Reason.DidNotGetMoreCustomers]: 'Did not get more customers',
  [Reason.TooComplex]: "I couldn't figure out how to use it",
  [Reason.SwitchedService]: 'I found another tool I like better',
  [Reason.Other]: 'Other',
}

export const reasonPlaceholders: Record<Reason, string | null> = {
  [Reason.MissingIntegrations]: 'Which integrations would you like us to add?',
  [Reason.UnreliableContentGeneration]: 'What specifically was unreliable?',
  [Reason.UnreliableContentPublishing]: 'What specifically was unreliable?',
  [Reason.PlatformTooSlow]: 'What was slow?',
  [Reason.BadAiGenerations]: "Which content types weren't good enough for you?",
  [Reason.LackingEditingFeatures]: 'Which editing features were we missing?',
  [Reason.TooExpensive]: 'What would make the price work for you?',
  [Reason.DidNotGetMoreCustomers]: 'Tell us more',
  [Reason.TooComplex]: 'What specifically was difficult to use?',
  [Reason.SwitchedService]: 'Which tool are you switching to?',
  [Reason.Other]: 'Please add additional reasons',
}

export const stripeCancellationReasonForReason = (reason: Reason) => {
  switch (reason) {
    case Reason.MissingIntegrations:
    case Reason.LackingEditingFeatures:
      return CancellationReason.MissingFeatures
    case Reason.BadAiGenerations:
    case Reason.UnreliableContentGeneration:
    case Reason.UnreliableContentPublishing:
    case Reason.PlatformTooSlow:
      return CancellationReason.LowQuality
    case Reason.DidNotGetMoreCustomers:
      return CancellationReason.Unused
    case Reason.TooExpensive:
      return CancellationReason.TooExpensive
    case Reason.TooComplex:
      return CancellationReason.TooComplex
    case Reason.SwitchedService:
      return CancellationReason.SwitchedService
    case Reason.Other:
      return CancellationReason.Other
    default:
      return CancellationReason.Other
  }
}

export const ProvideReason = ({
  onCancel,
  onNext,
  initialSelectedReasons = [],
  initialAdditionalFeedback = '',
}: Props) => {
  const [selectedReasons, setSelectedReasons] = useState<SelectedReason[]>(initialSelectedReasons)
  const [additionalFeedback, setAdditionalFeedback] = useState(initialAdditionalFeedback || '')
  const [error, setError] = useState(false)

  const handleNext = () => {
    if (selectedReasons.length === 0) {
      setError(true)
      return
    }
    onNext(selectedReasons, additionalFeedback)
  }

  const handleCheckboxChange = (reason: Reason) => {
    setError(false)

    if (selectedReasons.map((r) => r.reason).includes(reason)) {
      setSelectedReasons(selectedReasons.filter((r) => r.reason !== reason))
      return
    }
    const subItems = REASON_SUB_ITEMS[reason] ? [] : undefined
    setSelectedReasons([...selectedReasons, { reason, description: '', subItems }])
  }

  const handleInputChange = (value: string, reason: Reason) => {
    setSelectedReasons(
      selectedReasons.map((selectedReason) =>
        selectedReason.reason === reason ? { ...selectedReason, description: value } : selectedReason,
      ),
    )
  }

  const handleSubItemToggle = (reason: Reason, subItemKey: string) => {
    setSelectedReasons(
      selectedReasons.map((selectedReason) => {
        if (selectedReason.reason !== reason) return selectedReason
        const current = selectedReason.subItems ?? []
        const next = current.includes(subItemKey) ? current.filter((k) => k !== subItemKey) : [...current, subItemKey]
        return { ...selectedReason, subItems: next }
      }),
    )
  }

  return (
    <div className={Styles.container}>
      <Modal.Header
        title="Help us improve, why are you cancelling?"
        onClose={onCancel}
        subHeader={
          <>
            {error ? (
              <Text variant="secondary" color="var(--red-70)">
                Must select at least one option *
              </Text>
            ) : (
              <Text variant="secondary" color="var(--dark-60)">
                Help us improve by letting us know why you're leaving. Select all that apply.
              </Text>
            )}
          </>
        }
      />

      <div className={Styles.content}>
        <div className={Styles.reasons}>
          {Object.values(Reason).map((reason) => {
            const isSelected = selectedReasons.map((r) => r.reason).includes(reason)
            const subItemsConfig = REASON_SUB_ITEMS[reason]
            const selectedReason = selectedReasons.find((r) => r.reason === reason)

            return (
              <div className={Styles.reasonContainer} key={reason}>
                <Checkbox
                  size="md"
                  isSelected={isSelected}
                  onChange={() => handleCheckboxChange(reason)}
                  aria-label={reasonLabels[reason]}
                >
                  <span className={Styles.checkboxText}>
                    <Text variant="primary" color="var(--dark-80)">
                      {reasonLabels[reason]}
                    </Text>
                  </span>
                </Checkbox>
                {isSelected && subItemsConfig && (
                  <div className={Styles.subItems}>
                    {Object.entries(subItemsConfig).map(([subItemKey, subItemLabel]) => (
                      <Checkbox
                        key={subItemKey}
                        size="md"
                        isSelected={selectedReason?.subItems?.includes(subItemKey) ?? false}
                        onChange={() => handleSubItemToggle(reason, subItemKey)}
                        aria-label={subItemLabel}
                      >
                        <span className={Styles.checkboxText}>
                          <Text variant="primary" color="var(--dark-80)">
                            {subItemLabel}
                          </Text>
                        </span>
                      </Checkbox>
                    ))}
                  </div>
                )}
                {isSelected && reasonPlaceholders[reason] && (
                  <div className={Styles.input}>
                    <Input
                      name={`reason-${reason}`}
                      onChange={(event) => handleInputChange(event.target.value, reason)}
                      placeholder={reasonPlaceholders[reason] || undefined}
                      value={selectedReason?.description || ''}
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <div className={Styles.feedbackSection}>
          <Heading level={4}>Additional feedback (optional)</Heading>
          <TextAreaField
            inputClassName={Styles.feedbackTextarea}
            placeholder="Tell us more about your experience..."
            value={additionalFeedback}
            onChange={(e) => setAdditionalFeedback(e.target.value)}
            rows={4}
          />
        </div>
      </div>

      <div className={Styles.footer}>
        <Button size="lg" variant="tertiary" onPress={onCancel}>
          Cancel
        </Button>
        <Button size="lg" variant="primary" onPress={handleNext} isDisabled={error || selectedReasons.length === 0}>
          Next
        </Button>
      </div>
    </div>
  )
}
